(() => {
  if (globalThis.OsoPairingPlayer) return;
  let youtubeTask;
  function youtubeAPI() {
    if (globalThis.YT?.Player) return Promise.resolve(globalThis.YT);
    if (youtubeTask) return youtubeTask;
    youtubeTask = new Promise((resolve, reject) => {
      let script = document.querySelector('script[data-oso-youtube-api]');
      const previous = globalThis.onYouTubeIframeAPIReady;
      let settled = false;
      const finish = error => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (error) { youtubeTask = null; script?.remove(); reject(error); }
        else resolve(globalThis.YT);
      };
      const timer = setTimeout(() => finish(Error('api_timeout')), 20000);
      globalThis.onYouTubeIframeAPIReady = () => {
        try { previous?.(); } catch {}
        if (globalThis.YT?.Player) finish(); else finish(Error('api_unavailable'));
      };
      if (!script) {
        script = document.createElement('script');
        script.dataset.osoYoutubeApi = '';
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        script.addEventListener('error', () => finish(Error('api_unavailable')), { once: true });
        document.head.append(script);
      }
    });
    return youtubeTask;
  }
  // iOS ignores media.volume and YouTube setVolume: audio files go through a Web Audio gain there, YouTube gets mute only.
  // Recent iOS reads the volume back as set while still ignoring it, so detect iPhone/iPad (incl. iPadOS desktop UA) too.
  const appleTouch = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const volumeLocked = appleTouch || (() => { try { const probe = document.createElement('audio'); probe.volume = 0.5; return probe.volume !== 0.5; } catch { return false; } })();
  class OsoPairingPlayer {
    static get volumeLocked() { return volumeLocked; }
    static source(product) {
      const candidate = product?.audio ? { kind: 'audio', url: product.audio } : product?.sound;
      if (!candidate || typeof candidate.url !== 'string' || !candidate.url.trim()) return null;
      let url;
      try { url = new URL(candidate.url, location.origin); } catch { return null; }
      if (!['https:', 'http:'].includes(url.protocol)) return null;
      const host = url.hostname.toLowerCase().replace(/^www\./, '');
      if (candidate.kind === 'audio') {
        if (['youtube.com', 'youtube-nocookie.com', 'youtu.be', 'vimeo.com', 'player.vimeo.com'].includes(host)) return null;
        return { kind: 'audio', url: url.href };
      }
      if (candidate.kind !== 'youtube' || !['youtube.com', 'youtube-nocookie.com', 'youtu.be'].includes(host)) return null;
      const id = host === 'youtu.be' ? url.pathname.split('/')[1] : /^\/embed\//.test(url.pathname) ? url.pathname.split('/')[2] : url.searchParams.get('v');
      return /^[A-Za-z0-9_-]{11}$/.test(id || '') ? { kind: 'youtube', id, url: 'https://www.youtube-nocookie.com/embed/' + id } : null;
    }
    constructor(options = {}) {
      this.owner = options.owner || this;
      this.onChange = options.onChange || (() => {});
      this.makeAudio = options.makeAudio || (() => new Audio());
      this.loadYouTube = options.loadYouTube || youtubeAPI;
      this.isVisible = options.isVisible || (track => {
        const frame = track.frame || track.host;
        if (!frame || frame.isConnected === false || document.hidden) return false;
        const rect = frame.getBoundingClientRect();
        if (rect.width < 200 || rect.height < 200) return false;
        for (let node = frame; node && node !== document; node = node.parentElement) {
          const style = getComputedStyle(node);
          if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
        }
        return true;
      });
      this.later = options.setTimeout || globalThis.setTimeout.bind(globalThis);
      this.cancel = options.clearTimeout || globalThis.clearTimeout.bind(globalThis);
      this.timeout = options.timeout || 20000;
      this.tracks = new Map();
      this.events = new AbortController();
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.pauseAll(); }, { signal: this.events.signal });
      globalThis.addEventListener?.('pagehide', () => this.pauseAll(), { signal: this.events.signal });
      const checkVisibility = () => this.tracks.forEach(track => {
        if (track.kind === 'youtube' && track.wanted && !this.isVisible(track)) this.pause(track.id, 'offscreen');
      });
      globalThis.addEventListener?.('resize', checkVisibility, { passive: true, signal: this.events.signal });
      if (options.pollInterval !== 0) this.clock = setInterval(() => {
        if ([...this.tracks.values()].some(track => track.state === 'playing')) this.emit();
      }, options.pollInterval || 1000);
    }
    live(track) { return !this.destroyed && this.tracks.get(track.id) === track; }
    state(track, state, error = '') {
      if (!this.live(track)) return;
      track.state = state;
      track.error = error;
      this.emit();
    }
    snapshot() {
      const tracks = [...this.tracks.values()].map(track => {
        let currentTime = track.audio?.currentTime || 0, duration = track.audio?.duration || 0;
        try {
          if (track.player && track.ready) {
            currentTime = track.player.getCurrentTime?.() || 0;
            duration = track.player.getDuration?.() || 0;
          }
        } catch {}
        return {
          id: track.id, productId: track.productId, kind: track.kind, state: track.state,
          ready: track.ready, error: track.error, volume: track.volume, muted: track.muted,
          currentTime: Number.isFinite(currentTime) ? currentTime : 0,
          duration: Number.isFinite(duration) ? duration : 0
        };
      });
      return {
        tracks,
        playing: tracks.filter(track => track.state === 'playing').length,
        loading: tracks.filter(track => ['loading', 'buffering'].includes(track.state)).length,
        blocked: tracks.filter(track => track.state === 'blocked').length,
        errors: tracks.filter(track => ['error', 'unavailable'].includes(track.state)).length
      };
    }
    emit() { if (!this.destroyed) this.onChange(this.snapshot()); }
    finish(track, success) {
      if (track.timer) this.cancel(track.timer);
      track.timer = null;
      if (track.pending) { const resolve = track.pending; track.pending = null; resolve(success); }
    }
    fail(track, error) {
      if (!this.live(track)) return;
      track.wanted = false;
      track.token++;
      if (track.readyTimer) this.cancel(track.readyTimer);
      track.readyTimer = null;
      if (track.kind === 'youtube' && !track.ready) {
        track.prepareToken++;
        try { track.player?.destroy?.(); } catch {}
        track.frame?.remove();
        track.frame = null;
        track.player = null;
      }
      try { track.audio?.pause(); track.player?.pauseVideo?.(); } catch {}
      this.finish(track, false);
      this.state(track, error === 'not_allowed' ? 'blocked' : 'error', error);
    }
    watch(track) {
      if (track.timer) this.cancel(track.timer);
      track.timer = this.later(() => {
        if (this.live(track) && track.wanted) this.fail(track, 'play_timeout');
      }, this.timeout);
    }
    setTrack(id, product, host) {
      if (this.destroyed) return null;
      id = String(id);
      const source = OsoPairingPlayer.source(product);
      const key = source ? source.kind + ':' + source.url : '';
      const existing = this.tracks.get(id);
      if (existing && existing.key === key && existing.productId === String(product?.id) && existing.host === host) return this.snapshot();
      this.remove(id);
      const track = {
        id, productId: String(product?.id || ''), product, host, key, source,
        kind: source?.kind || 'unavailable', ready: false, state: source ? 'loading' : 'unavailable',
        volume: 0.7, muted: false, error: source ? '' : 'unsupported', wanted: false, token: 0, prepareToken: 0, events: new AbortController()
      };
      this.tracks.set(id, track);
      if (!source) { this.emit(); return this.snapshot(); }
      if (source.kind === 'audio') this.prepareAudio(track);
      else this.prepareYoutube(track, product);
      this.emit();
      return this.snapshot();
    }
    prepareAudio(track) {
      const audio = this.makeAudio();
      track.audio = audio;
      audio.preload = 'metadata';
      audio.playsInline = true;
      if (volumeLocked) audio.crossOrigin = 'anonymous';
      audio.volume = track.volume;
      const on = (event, action) => audio.addEventListener(event, () => { if (this.live(track)) action(); }, { signal: track.events.signal });
      on('playing', () => {
        if (!track.wanted) { audio.pause(); return; }
        this.finish(track, true);
        this.state(track, 'playing');
      });
      on('waiting', () => { if (track.wanted) { this.state(track, 'buffering'); this.watch(track); } });
      on('pause', () => {
        if (!audio.paused || ['error', 'blocked', 'ended', 'offscreen'].includes(track.state)) return;
        track.wanted = false;
        this.finish(track, false);
        this.state(track, 'paused');
      });
      on('ended', () => {
        track.wanted = false;
        this.finish(track, true);
        this.state(track, 'ended');
      });
      on('error', () => this.fail(track, ({ 1: 'aborted', 2: 'network', 3: 'decode', 4: 'unsupported' })[audio.error?.code] || 'media'));
      ['timeupdate', 'loadedmetadata', 'durationchange'].forEach(event => on(event, () => this.emit()));
      audio.src = track.source.url;
      track.ready = true;
      track.state = 'ready';
    }
    async prepareYoutube(track, product) {
      const preparation = ++track.prepareToken;
      if (!track.host) { this.fail(track, 'missing_host'); return; }
      const frame = document.createElement('iframe');
      frame.title = product?.title || 'YouTube video';
      frame.width = '100%';
      frame.height = '200';
      frame.style.width = '100%';
      frame.style.minWidth = '200px';
      frame.style.minHeight = '200px';
      frame.style.aspectRatio = '16 / 9';
      frame.style.border = '0';
      frame.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
      frame.allowFullscreen = true;
      frame.referrerPolicy = 'strict-origin-when-cross-origin';
      const url = new URL(track.source.url);
      Object.entries({ enablejsapi: '1', autoplay: '0', controls: '1', playsinline: '1', rel: '0', origin: location.origin }).forEach(([key, value]) => url.searchParams.set(key, value));
      frame.src = url.href;
      track.frame = frame;
      track.host.append(frame);
      track.readyTimer = this.later(() => { if (this.live(track) && !track.ready) this.fail(track, 'player_timeout'); }, this.timeout);
      try {
        const YT = await this.loadYouTube();
        if (!this.live(track) || preparation !== track.prepareToken) return;
        track.player = new YT.Player(frame, { events: {
          onReady: event => {
            if (!this.live(track) || preparation !== track.prepareToken || track.ready) return;
            track.player = event.target;
            if (track.readyTimer) this.cancel(track.readyTimer);
            track.readyTimer = null;
            track.ready = true;
            track.player.setVolume(Math.round(track.volume * 100));
            if (track.muted) track.player.mute();
            this.state(track, 'ready');
          },
          onStateChange: event => { if (preparation === track.prepareToken) this.youtubeState(track, event.data); },
          onError: event => { if (preparation === track.prepareToken) this.fail(track, 'youtube_' + event.data); },
          onAutoplayBlocked: () => { if (preparation === track.prepareToken) this.fail(track, 'not_allowed'); }
        } });
      } catch (error) {
        if (!this.live(track) || preparation !== track.prepareToken) return;
        frame.remove();
        track.frame = null;
        this.fail(track, error.message || 'api_unavailable');
      }
    }
    youtubeState(track, value) {
      if (!this.live(track)) return;
      if (value === 1) {
        if (track.pauseBarrier) { try { track.player.pauseVideo(); } catch {} return; }
        if (!this.isVisible(track)) { this.pause(track.id, 'offscreen'); return; }
        if (!track.wanted) this.announce();
        track.wanted = true;
        this.finish(track, true);
        this.state(track, 'playing');
      } else if (value === 3) {
        if (track.wanted) { this.state(track, 'buffering'); this.watch(track); }
      } else if (value === 2) {
        track.pauseBarrier = false;
        track.wanted = false;
        this.finish(track, false);
        if (!['error', 'blocked', 'offscreen'].includes(track.state)) this.state(track, 'paused');
      } else if (value === 0) {
        track.pauseBarrier = false;
        track.wanted = false;
        this.finish(track, true);
        this.state(track, 'ended');
      } else if (value === 5 && track.ready && !track.wanted) {
        track.pauseBarrier = false;
        if (!['error', 'blocked', 'offscreen'].includes(track.state)) this.state(track, 'ready');
      }
    }
    announce() { document.dispatchEvent(new CustomEvent('oso:audio-start', { detail: { owner: this.owner } })); }
    play(id, announce = true) {
      const track = this.tracks.get(String(id));
      if (!track || !this.live(track) || !track.ready) return Promise.resolve(false);
      if (track.state === 'playing') return Promise.resolve(true);
      if (track.kind === 'youtube' && !this.isVisible(track)) { this.state(track, 'offscreen', 'offscreen'); return Promise.resolve(false); }
      if (announce) this.announce();
      const previousState = track.state;
      this.finish(track, false);
      const token = ++track.token;
      track.wanted = true;
      track.pauseBarrier = false;
      const pending = new Promise(resolve => { track.pending = resolve; });
      this.state(track, 'loading');
      this.watch(track);
      try {
        if (track.kind === 'audio') {
          this.gain(track);
          if (track.audio.error) track.audio.load();
          if (track.audio.ended) track.audio.currentTime = 0;
          Promise.resolve(track.audio.play()).then(() => {
            if (!this.live(track) || token !== track.token || !track.wanted) return;
            if (!track.audio.paused) { this.finish(track, true); this.state(track, 'playing'); }
          }, error => {
            if (this.live(track) && token === track.token && track.wanted) this.fail(track, error?.name === 'NotAllowedError' ? 'not_allowed' : 'media');
          });
        } else {
          if (previousState === 'ended') track.player.seekTo(0, true);
          track.player.playVideo();
        }
      } catch (error) { this.fail(track, error?.name === 'NotAllowedError' ? 'not_allowed' : 'media'); }
      return pending;
    }
    toggle(id) {
      const track = this.tracks.get(String(id));
      if (!track) return Promise.resolve(false);
      if (track.wanted || track.state === 'playing') { this.pause(id); return Promise.resolve(false); }
      if (track.state === 'error' && !track.ready) { this.retry(id); return Promise.resolve(false); }
      return this.play(id);
    }
    playAll(ids = [...this.tracks.keys()]) {
      const selected = [...new Set(ids.map(String))];
      this.announce();
      return Promise.all(selected.map(id => this.play(id, false)));
    }
    pause(id, state = 'paused') {
      const track = this.tracks.get(String(id));
      if (!track || !this.live(track)) return;
      track.token++;
      const wasActive = track.wanted || ['playing', 'buffering', 'loading'].includes(track.state);
      track.wanted = false;
      track.pauseBarrier = track.kind === 'youtube' && track.ready && wasActive;
      this.finish(track, false);
      try { track.audio?.pause(); if (track.ready) track.player?.pauseVideo?.(); } catch {}
      if (track.ready) this.state(track, state, state === 'offscreen' ? 'offscreen' : '');
    }
    pauseAll() { this.tracks.forEach(track => this.pause(track.id)); }
    stop(id) {
      const track = this.tracks.get(String(id));
      if (!track) return;
      this.pause(id);
      try {
        if (track.audio) track.audio.currentTime = 0;
        if (track.ready && track.player) { track.player.seekTo(0, true); track.player.pauseVideo(); }
      } catch {}
      this.emit();
    }
    stopAll() { this.tracks.forEach(track => this.stop(track.id)); }
    volume(id, value) {
      const track = this.tracks.get(String(id)), volume = Number(value);
      if (!track || !Number.isFinite(volume)) return;
      track.volume = Math.max(0, Math.min(1, volume));
      try {
        if (track.audio) track.audio.volume = track.volume;
        if (track.gainNode) track.gainNode.gain.value = track.volume;
        if (track.ready) track.player?.setVolume?.(Math.round(track.volume * 100));
      } catch {}
      this.emit();
    }
    mute(id, muted) {
      const track = this.tracks.get(String(id));
      if (!track) return;
      track.muted = !!muted;
      try {
        if (track.audio) track.audio.muted = track.muted;
        if (track.ready && track.player) track.muted ? track.player.mute() : track.player.unMute();
      } catch {}
      this.emit();
    }
    // Called inside the play gesture so iOS lets the context start
    gain(track) {
      if (!volumeLocked || !track.audio) return;
      const Context = globalThis.AudioContext || globalThis.webkitAudioContext;
      if (!Context) return;
      try {
        if (navigator.audioSession) navigator.audioSession.type = 'playback';
        const context = OsoPairingPlayer.context || (OsoPairingPlayer.context = new Context());
        if (context.state !== 'running') context.resume();
        if (track.gainNode) return;
        track.gainNode = context.createGain();
        track.gainNode.gain.value = track.volume;
        context.createMediaElementSource(track.audio).connect(track.gainNode).connect(context.destination);
      } catch {}
    }
    retry(id) {
      const track = this.tracks.get(String(id));
      if (!track) return;
      const { product, host, volume, muted } = track;
      this.remove(id);
      this.setTrack(id, product, host);
      this.volume(id, volume);
      this.mute(id, muted);
    }
    ownsMedia(element) { return [...this.tracks.values()].some(track => track.audio === element || track.frame === element); }
    remove(id) {
      const track = this.tracks.get(String(id));
      if (!track) return;
      track.token++;
      track.wanted = false;
      if (track.readyTimer) this.cancel(track.readyTimer);
      track.readyTimer = null;
      this.finish(track, false);
      this.tracks.delete(track.id);
      track.events.abort();
      try {
        if (track.audio) { track.audio.pause(); track.audio.removeAttribute('src'); track.audio.load(); }
        track.player?.destroy?.();
      } catch {}
      track.frame?.remove();
      this.emit();
    }
    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.events.abort();
      clearInterval(this.clock);
      [...this.tracks.keys()].forEach(id => this.remove(id));
    }
  }
  globalThis.OsoPairingPlayer = OsoPairingPlayer;
})();
